import { useEffect, useState } from 'react';
import { usersApi } from '../api';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import UserModal from '../components/UserModal';

const roleLabels: Record<string, string> = {
  ADMIN: 'Администратор',
  MANAGER: 'Менеджер',
  VIEWER: 'Наблюдатель',
};

const roleBadgeClass: Record<string, string> = {
  ADMIN: 'badge-danger',
  MANAGER: 'badge-info',
  VIEWER: 'badge-default',
};

export default function AdminPage() {
  const [users, setUsers] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [page, setPage] = useState(1);
  const limit = 20;

  const load = async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page, limit };
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;
      const res = await usersApi.list(params);
      setUsers(res.data.data);
      setTotal(res.data.total);
    } catch (e) {
      console.error('Load users error:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [search, roleFilter, page]);
  useEffect(() => { setPage(1); }, [search, roleFilter]);

  const handleDeactivate = async (id: string) => {
    if (!confirm('Деактивировать пользователя?')) return;
    try {
      await usersApi.remove(id);
      load();
    } catch (e: any) {
      alert(e.response?.data?.error || 'Ошибка деактивации');
    }
  };

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Пользователи</h2>
        <button className="btn btn-primary" onClick={() => { setEditing(null); setShowModal(true); }}>
          <Plus size={18} /> Добавить пользователя
        </button>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="toolbar" style={{ margin: 0, width: '100%', display: 'flex', gap: 12 }}>
            <input
              className="search-input"
              placeholder="Поиск по имени, фамилии или email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ flex: 1 }}
            />
            <select
              className="form-select"
              value={roleFilter}
              onChange={e => setRoleFilter(e.target.value)}
              style={{ width: 200 }}
            >
              <option value="">Все роли</option>
              <option value="ADMIN">Администратор</option>
              <option value="MANAGER">Менеджер</option>
              <option value="VIEWER">Наблюдатель</option>
            </select>
          </div>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center' }}>Загрузка...</div>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Имя</th>
                    <th>Email</th>
                    <th>Роль</th>
                    <th>Телефон</th>
                    <th>Статус</th>
                    <th>Последний вход</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--color-text-secondary)' }}>
                      Пользователи не найдены
                    </td></tr>
                  )}
                  {users.map(u => (
                    <tr key={u.id}>
                      <td style={{ fontWeight: 600 }}>{u.firstName} {u.lastName}</td>
                      <td className="text-muted">{u.email}</td>
                      <td><span className={`badge ${roleBadgeClass[u.role] || 'badge-default'}`}>{roleLabels[u.role] || u.role}</span></td>
                      <td>{u.phone || '—'}</td>
                      <td>
                        <span className={`badge ${u.isActive ? 'badge-success' : 'badge-danger'}`}>
                          {u.isActive ? 'Активен' : 'Заблокирован'}
                        </span>
                      </td>
                      <td className="text-muted">{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString('ru-RU') : '—'}</td>
                      <td>
                        <div className="flex gap-2">
                          <button className="header-icon-btn" style={{ width: 32, height: 32 }} onClick={() => { setEditing(u); setShowModal(true); }}>
                            <Pencil size={14} />
                          </button>
                          <button className="header-icon-btn" style={{ width: 32, height: 32, color: 'var(--color-danger)' }} onClick={() => handleDeactivate(u.id)}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="card-footer" style={{ padding: '16px 24px', borderTop: '1px solid var(--color-border-soft)' }}>
          <div className="pagination">
            <button className="btn btn-secondary btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Назад</button>
            <div className="flex gap-2" style={{ margin: '0 16px' }}>
              <span style={{ fontSize: 14, fontWeight: 600 }}>Страница {page}</span>
            </div>
            <button className="btn btn-secondary btn-sm" disabled={page * limit >= total} onClick={() => setPage(p => p + 1)}>Далее</button>
          </div>
        </div>
      </div>

      {showModal && (
        <UserModal
          user={editing}
          onClose={() => setShowModal(false)}
          onSave={() => { setShowModal(false); load(); }}
        />
      )}
    </div>
  );
}
